import axios, { AxiosRequestConfig, ResponseType } from 'axios';
import { AuthHelper } from 'src/components/Auth/AuthHelper';
import { AuthConfiguration } from 'src/components/Auth/authConfiguration';
import { env } from 'src/config/env';

export const sendRequest = async (
  endpoint: string,
  method?: 'GET' | 'POST' | 'DELETE',
  data?: any,
  responseType?: ResponseType,
  fullResponse = false,
): Promise<any> => {
  const apiUrl = env.isDev ? env.apiAddress : `${location.origin}/api/v1.0/`;
  const url = `${apiUrl}${endpoint}`;
  const token = AuthHelper.getAccessToken();
  const headers = {
    'Authorization': "Bearer " + token,
  };
  const config: AxiosRequestConfig = {
    method: method ?? 'GET',
    url,
    headers,
    data,
  };

  if (responseType) config.responseType = responseType;

  try {
    const response = await axios(config);
    return fullResponse ? response : response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      let mp = new Map();
      error.response.headers['www-authenticate']
        .split(' ')
        .map((x: string) => {
          let keyValue = x.replace(',', '').replaceAll('"', '').split('=');
          mp.set(keyValue[0], keyValue[1]);
        });

      AuthHelper.removeAccessToken();

      const authConfig: AuthConfiguration = {
        clientId: mp.get('client_id'),
        authority: mp.get('authorization_uri'),
        domain: mp.get('domain'),
        apiScopes: [mp.get('scope')],
        redirectUri: mp.get('redirect_uri'),
        postLogoutRedirectUri: mp.get('landing_page_uri'),
      };
      await AuthHelper.init(authConfig);
    } else if (error.response?.status === 409 && error.response?.data?.errors[0].messageResKey==="FILE_IN_USE") {
      return  error.response?.data?.errors[0].message;
    } else if (error.response?.data?.errors?.length) {
      const errorMessages = error.response.data.errors.map((err: { message: string }) => err.message).join(' ');
      throw new Error(errorMessages);
    } else {
      throw error;
    }
  }
};

export const getFileContentByRangesRequest = async (endpoint: string): Promise<any> => {
  const apiUrl = env.isDev ? env.apiAddress : `${location.origin}/api/v1.0/`;
  const url = `${apiUrl}${endpoint}`;
  const headers = {
    'Authorization': "Bearer " + AuthHelper.getAccessToken(),
  };

  try {
    const headResponse = await axios({
      method: 'HEAD',
      url,
      headers,
    });
    const rangeSize = 1024 * 1000 * 20; // 20 MB per call
    const contentLength = +(headResponse.headers['content-length'] ?? 0);
    const unit = headResponse.headers['accept-ranges'];

    const responses: ArrayBuffer[] = [];

    let startPosition = 0;
    while (startPosition < contentLength) {
      const headersWithRange = {
        ...headers, ...{
          'Range': `${unit}=${startPosition}-${startPosition + rangeSize - 1}`,
        },
      };
      const range = await axios({
        method: 'GET',
        url,
        headers: headersWithRange,
        responseType: 'arraybuffer',
      });
      responses.push(range.data);
      startPosition += rangeSize;
    }
    return new Blob(responses);
  } catch (error: any) {
    if (error.response?.status === 401) {
      let mp = new Map();
      error.response.headers['www-authenticate']
        .split(' ')
        .map((x: string) => {
          let keyValue = x.replace(',', '').replaceAll('"', '').split('=');
          mp.set(keyValue[0], keyValue[1]);
        });

      AuthHelper.removeAccessToken();

      const authConfig: AuthConfiguration = {
        clientId: mp.get('client_id'),
        authority: mp.get('authorization_uri'),
        domain: mp.get('domain'),
        apiScopes: [mp.get('scope')],
        redirectUri: mp.get('redirect_uri'),
        postLogoutRedirectUri: mp.get('landing_page_uri'),
      };
      await AuthHelper.init(authConfig);
    } else if (error.response?.data?.errors?.length) {
      const errorMessages = error.response.data.errors.map((err: { message: string }) => err.message).join(' ');
      throw new Error(errorMessages);
    } else {
      throw error;
    }
  }
};

export const downloadFile = (blob: any, name: string) => {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', name);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};
